// Password validation utilities for enhanced security without Supabase Pro

export interface PasswordValidationResult {
  isValid: boolean;
  score: number; // 0-5 strength score
  feedback: string[];
}

/**
 * Validates password strength without external API calls
 */
export function validatePasswordStrength(password: string): PasswordValidationResult {
  const feedback: string[] = [];
  let score = 0;

  // Basic requirements
  if (password.length < 8) {
    feedback.push("Password must be at least 8 characters long");
  } else {
    score += 1;
  }

  if (password.length >= 12) {
    score += 1;
  }

  // Character variety
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  // Common patterns to avoid
  const commonPatterns = [
    /(.)\1{2,}/, // repeated characters
    /123456|abcdef|qwerty/i, // common sequences
    /password|admin|user/i, // common words
  ];

  commonPatterns.forEach(pattern => {
    if (pattern.test(password)) {
      feedback.push("Avoid common patterns and repeated characters");
      score = Math.max(0, score - 1);
    }
  });

  // Feedback based on score
  if (score < 3) {
    feedback.push("Password is too weak. Use a mix of uppercase, lowercase, numbers, and symbols");
  } else if (score < 4) {
    feedback.push("Password is moderate. Consider adding more character variety");
  }

  return {
    isValid: score >= 3 && password.length >= 8,
    score,
    feedback
  };
}

/**
 * Common leaked passwords to check against (small sample)
 */
const COMMON_LEAKED_PASSWORDS = new Set([
  "123456", "password", "123456789", "12345678", "12345", "1234567",
  "qwerty", "abc123", "million2", "000000", "1234567890", "123123",
  "admin", "letmein", "welcome", "monkey", "login", "dragon", "pass",
  "master", "hello", "freedom", "whatever", "qazwsx", "trustno1"
]);

/**
 * Enhanced check against common leaked passwords
 */
export function isCommonLeakedPassword(password: string): boolean {
  return COMMON_LEAKED_PASSWORDS.has(password.toLowerCase());
}

/**
 * Check for keyboard patterns and common substitutions
 */
function hasKeyboardPattern(password: string): boolean {
  const patterns = [
    /qwerty/i, /asdfgh/i, /zxcvbn/i, /123456/i, /987654/i,
    /qwertyuiop/i, /asdfghjkl/i, /zxcvbnm/i
  ];
  return patterns.some(pattern => pattern.test(password));
}

/**
 * Complete password validation with enhanced security checks
 */
export function validatePassword(password: string): PasswordValidationResult {
  const strengthResult = validatePasswordStrength(password);
  
  if (isCommonLeakedPassword(password)) {
    strengthResult.feedback.push("This password has been found in data breaches. Please choose a different one.");
    strengthResult.isValid = false;
    strengthResult.score = 0;
  }

  if (hasKeyboardPattern(password)) {
    strengthResult.feedback.push("Avoid keyboard patterns like 'qwerty' or '123456'.");
    strengthResult.score = Math.max(0, strengthResult.score - 1);
  }

  return strengthResult;
}